import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import ChallengeAnalysisChat from './ChallengeAnalysisChat';
import { getUserId } from '../firebase';
import { canAccessDevs } from './firestoreFunctions';
import ActivityMessage, { isJsonContent, parseActivityMessage } from './ActivityMessage';

// Reusable panel to display a challenge's details.
// Props:
// - challenge: the challenge object to render
// - onClose?: optional callback to close/hide the panel
// - onRerun?: optional callback to trigger a rerun flow
const ChallengeDetailsPanel = ({ challenge, onClose, onRerun }) => {
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showActivityDetails, setShowActivityDetails] = useState(false);
  const [selectedActivityDetails, setSelectedActivityDetails] = useState(null);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLogText, setActivityLogText] = useState('');
  const [copyingImage, setCopyingImage] = useState(false);
  const [copyImageStatus, setCopyImageStatus] = useState('');
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [lastAssistantMessage, setLastAssistantMessage] = useState('');
  const [hasDevAccess, setHasDevAccess] = useState(false);
  const [isCheckingDevAccess, setIsCheckingDevAccess] = useState(true);
  const isZoomed = zoomLevel > 1;
  const hasProposedPrompt =
    typeof lastAssistantMessage === 'string' && lastAssistantMessage.trim().length > 0;

  useEffect(() => {
    let cancelled = false;

    const verifyDevAccess = async () => {
      setIsCheckingDevAccess(true);
      try {
        const uid = await getUserId();
        if (!uid) {
          if (!cancelled) setHasDevAccess(false);
          return;
        }

        const allowed = await canAccessDevs(uid);
        if (!cancelled) setHasDevAccess(Boolean(allowed));
      } catch (error) {
        if (!cancelled) setHasDevAccess(false);
      } finally {
        if (!cancelled) setIsCheckingDevAccess(false);
      }
    };

    verifyDevAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(dateString);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };


  const copyTextToClipboard = async (text) => {
    try {
      if (text == null) return false;
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {}
    try {
      const textarea = document.createElement('textarea');
      textarea.value = String(text);
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '1px';
      textarea.style.height = '1px';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      return false;
    }
  };

  const buildActivityLogText = () => {
    if (!challenge || !challenge.activity_history) return '';
    const lines = Array.isArray(challenge.activity_history)
      ? challenge.activity_history.map((msg) => {
          const parsed = parseActivityMessage(msg);
          const title = parsed.activity || '';
          const details = parsed.hasDetails
            ? typeof parsed.details === 'string'
              ? parsed.details
              : String(parsed.details)
            : '';
          return `${title}\n${details}`;
        })
      : [String(challenge.activity_history)];
    return lines.join('\n\n');
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showScreenshot) {
          setShowScreenshot(false);
          return;
        }
        if (showActivityDetails) {
          setShowActivityDetails(false);
          setSelectedActivityDetails(null);
          return;
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showScreenshot, showActivityDetails]);

  useEffect(() => {
    if (showScreenshot) {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
    }
  }, [showScreenshot]);

  useEffect(() => {
    setLastAssistantMessage('');
  }, [challenge?.id]);

  const handleAssistantMessage = useCallback(
    (message) => {
      if (typeof message !== 'string') {
        setLastAssistantMessage('');
        return;
      }
      setLastAssistantMessage(message.trim());
    },
    [setLastAssistantMessage],
  );

  useEffect(() => {
    setIsAnalysisOpen(false);
  }, [challenge?.id]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev * 1.2, 5));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev / 1.2, 0.5));
  const handleZoomReset = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };
  const handleMouseDown = (e) => {
    if (isZoomed) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPanPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const copyImageToClipboard = async () => {
    if (!challenge?.image_base64) {
      console.warn('[CopyImage][Panel] No image_base64 present on challenge');
      return;
    }
    setCopyImageStatus('');
    setCopyingImage(true);
    const dataUrlJpeg = `data:image/jpeg;base64,${challenge.image_base64}`;

    // Try permissions hint (best-effort)
    let permState = null;
    try {
      const perm = await (navigator.permissions && navigator.permissions.query ? navigator.permissions.query({ name: 'clipboard-write' }) : null);
      if (perm) {
        permState = perm.state;
        if (perm.state === 'denied') {
          console.warn('[CopyImage][Panel] clipboard-write permission denied');
        } else {
          console.info('[CopyImage][Panel] clipboard-write permission state:', perm.state);
        }
      }
    } catch (_) {}

    const toPngBlob = async () => {
      try {
        const img = new Image();
        img.src = dataUrlJpeg;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        console.info('[CopyImage][Panel] Canvas PNG blob created:', !!blob);
        return blob;
      } catch (err) {
        console.warn('[CopyImage][Panel] toPngBlob failed:', err);
        return null;
      }
    };

    const writeBlob = async (blob) => {
      if (!blob) throw new Error('No image data');
      if (navigator.clipboard && typeof window.ClipboardItem !== 'undefined') {
        try {
          console.info('[CopyImage][Panel] Attempting navigator.clipboard.write with type:', blob.type);
          await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
          console.info('[CopyImage][Panel] navigator.clipboard.write success');
          return true;
        } catch (err) {
          console.warn('[CopyImage][Panel] navigator.clipboard.write failed:', err);
          throw err;
        }
      }
      console.warn('[CopyImage][Panel] ClipboardItem or navigator.clipboard not available');
      return false;
    };

    const execCommandCopyFallback = async () => {
      try {
        const container = document.createElement('div');
        container.contentEditable = 'true';
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        const img = document.createElement('img');
        img.src = dataUrlJpeg;
        container.appendChild(img);
        document.body.appendChild(container);
        const range = document.createRange();
        range.selectNodeContents(container);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        const ok = document.execCommand('copy');
        sel.removeAllRanges();
        document.body.removeChild(container);
        console.info('[CopyImage][Panel] document.execCommand(copy) result:', ok);
        return ok;
      } catch (e) {
        console.warn('[CopyImage][Panel] execCommand fallback failed:', e);
        return false;
      }
    };

    let usedFallback = false;
    try {
      const pngBlob = await toPngBlob();
      if (pngBlob) {
        await writeBlob(pngBlob);
        setCopyImageStatus(permState === 'denied' ? 'Copied (fallback may be required); clipboard denied' : 'Copied to clipboard');
        console.info('[CopyImage][Panel] Copy success (PNG path)');
      } else {
        // Last resort: try direct fetch->blob (jpeg) then write
        console.info('[CopyImage][Panel] PNG blob not available, trying JPEG path');
        const jpegBlob = await (await fetch(dataUrlJpeg)).blob();
        await writeBlob(jpegBlob);
        setCopyImageStatus(permState === 'denied' ? 'Copied (fallback may be required); clipboard denied' : 'Copied to clipboard');
        console.info('[CopyImage][Panel] Copy success (JPEG path)');
      }
    } catch (err) {
      // Try execCommand fallback (copies as HTML)
      console.warn('[CopyImage][Panel] Blob write failed, trying execCommand fallback. Error:', err);
      const ok = await execCommandCopyFallback();
      if (ok) {
        usedFallback = true;
        setCopyImageStatus('Copied (HTML fallback) — may not paste into all apps');
        console.info('[CopyImage][Panel] Copy success (execCommand path)');
      } else {
        const msg = (err && err.name === 'NotAllowedError')
          ? 'Clipboard permission blocked. Try clicking the page and retry.'
          : 'Copy not supported here. Use HTTPS/localhost and latest Chrome.';
        setCopyImageStatus(msg);
        console.warn('[CopyImage][Panel] Copy failed completely:', msg);
      }
    } finally {
      setTimeout(() => setCopyImageStatus(''), 3000);
      setCopyingImage(false);
    }
  };

  if (!challenge) {
    return null;
  }

  if (isCheckingDevAccess) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="text-sm text-gray-600">Verifying developer permissions…</div>
      </div>
    );
  }

  if (!hasDevAccess) {
    return (
      <div className="p-4">
        <div className="rounded border border-yellow-200 bg-yellow-50 text-yellow-800 p-3">
          Challenge details are restricted to developer accounts. Contact support if you believe this is an error.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">Challenge Details</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
            title="Close"
          >
            Close
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Challenge ID</span>
            <p className="text-sm text-gray-900 dark:text-gray-100 break-all">{challenge.id}</p>
          </div>
          <div className="flex items-center gap-2">
            {challenge.status && (
              <div className={`inline-flex items-center gap-2 text-xs px-2 py-1 rounded ${getStatusColor(String(challenge.status))}`}>
                {getStatusIcon(String(challenge.status))}
                <span className="capitalize">{String(challenge.status)}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setIsAnalysisOpen(true)}
              className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700"
              title="Analyse challenge"
            >
              Analyse Challenge
            </button>
            {onRerun && (
              <>
                <button
                  onClick={() => {
                    if (onRerun) onRerun();
                  }}
                  className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  title="Rerun challenge"
                >
                  Rerun Challenge
                </button>
                <button
                  onClick={() => {
                    if (!onRerun || !hasProposedPrompt) return;
                    onRerun({ systemPrompt: lastAssistantMessage.trim() });
                  }}
                  disabled={!hasProposedPrompt}
                  className={`inline-flex items-center px-3 py-1 text-sm rounded-md text-white transition-colors ${
                    hasProposedPrompt
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-indigo-400 cursor-not-allowed opacity-70'
                  }`}
                  title={
                    hasProposedPrompt
                      ? 'Rerun using the latest assistant reply as the system prompt'
                      : 'Run an analysis to capture a proposed system prompt before rerunning'
                  }
                >
                  Rerun with Proposed System Prompt
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Created:</span>
            <p className="text-sm text-gray-900 dark:text-gray-100">{formatDate(challenge.created_at)}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chunks:</span>
            <p className="text-sm text-gray-900 dark:text-gray-100">{challenge.chunks ? challenge.chunks.length : 0}</p>
          </div>
          {challenge.guidance && String(challenge.guidance).trim().length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Guidance:</span>
              <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{challenge.guidance}</p>
            </div>
          )}
        </div>

        {challenge.image_base64 && (
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Screenshot:</span>
            <div className="mt-2 flex items-center flex-wrap gap-2">
              <button
                onClick={() => setShowScreenshot(true)}
                className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
              >
                📷 View Screenshot
              </button>
              <button
                onClick={copyImageToClipboard}
                disabled={copyingImage}
                className={`inline-flex items-center px-3 py-1 text-xs rounded transition-colors ${copyingImage ? 'bg-gray-300 text-gray-600' : 'bg-gray-700 text-white hover:bg-gray-800'}`}
                title="Copy image to clipboard"
              >
                📋 {copyingImage ? 'Copying…' : 'Copy Image'}
              </button>
              {challenge.activity_history && (
                <button
                  onClick={() => {
                    const txt = buildActivityLogText();
                    setActivityLogText(txt);
                    setShowActivityLog(true);
                  }}
                  className="inline-flex items-center px-3 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-800 transition-colors"
                >
                  📄 Activity Log
                </button>
              )}
              {copyImageStatus && (
                <span className="text-xs text-gray-600 dark:text-gray-300">{copyImageStatus}</span>
              )}
            </div>
          </div>
        )}

        {challenge.system_prompt && (
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">System Prompt:</span>
              <button
                className="text-xs px-2 py-1 border rounded text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                onClick={() => copyTextToClipboard(challenge.system_prompt)}
                title="Copy system prompt"
              >
                Copy
              </button>
            </div>
            <pre className="mt-2 max-h-56 overflow-y-auto text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 whitespace-pre-wrap break-words">{challenge.system_prompt}</pre>
          </div>
        )}

        {challenge.user_prompt_context && (
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">User Context:</span>
              <button
                className="text-xs px-2 py-1 border rounded text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                onClick={() => copyTextToClipboard(challenge.user_prompt_context)}
                title="Copy user context"
              >
                Copy
              </button>
            </div>
            <pre className="mt-2 max-h-40 overflow-y-auto text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 whitespace-pre-wrap break-words">{challenge.user_prompt_context}</pre>
          </div>
        )}

        {(() => {
          // Normalize chunks into an array of strings if present
          const chunksRaw = challenge?.chunks;
          let chunksList = [];
          if (Array.isArray(chunksRaw)) {
            chunksList = chunksRaw;
          } else if (typeof chunksRaw === 'string') {
            chunksList = [chunksRaw];
          } else if (chunksRaw && typeof chunksRaw === 'object') {
            try {
              chunksList = Object.values(chunksRaw).filter(Boolean);
            } catch (_) {
              chunksList = [];
            }
          }
          if (!chunksList || chunksList.length === 0) return null;
          const copyAll = () => copyTextToClipboard(chunksList.join('\n'));
          return (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Chunks:</span>
                <button
                  className="text-xs px-2 py-1 border rounded text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                  onClick={copyAll}
                  title="Copy all chunk IDs"
                >
                  Copy All
                </button>
              </div>
              <ul className="mt-2 max-h-40 overflow-y-auto text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 space-y-1">
                {chunksList.map((cid, idx) => (
                  <li key={`${cid}-${idx}`} className="flex items-center justify-between gap-2">
                    <code className="text-gray-800 dark:text-gray-100 break-all">{String(cid)}</code>
                    <button
                      className="text-[10px] px-2 py-0.5 border rounded text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 flex-shrink-0"
                      onClick={() => copyTextToClipboard(String(cid))}
                      title="Copy chunk ID"
                    >
                      Copy
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {challenge.activity_history && (
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Activity History:</span>
            <div className="max-h-96 overflow-y-auto space-y-1 pr-2">
              {Array.isArray(challenge.activity_history) ? (
                challenge.activity_history.map((activity, index) => (
                  <ActivityMessage
                    key={index}
                    message={activity}
                    isSelected={selectedActivityIndex === index}
                    onSelect={(parsed) => {
                      setSelectedActivityIndex(index);
                      setSelectedActivityDetails(parsed.details);
                      setShowActivityDetails(true);
                    }}
                  />
                ))
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {typeof challenge.activity_history === 'string'
                    ? challenge.activity_history
                    : 'No activity history available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Screenshot Lightbox */}
      {showScreenshot && challenge.image_base64 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowScreenshot(false);
          }}
        >
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <button
              onClick={() => setShowScreenshot(false)}
              className="absolute top-3 right-3 z-10 text-white bg-red-500 hover:bg-red-600 rounded-full w-8 h-8 flex items-center justify-center"
              title="Close"
            >
              ✕
            </button>

            <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-black/50 rounded px-2 py-1 text-white text-xs">
              <button onClick={handleZoomOut} className="px-2 py-1 hover:bg-white/20 rounded">−</button>
              <button onClick={handleZoomReset} className="px-2 py-1 hover:bg-white/20 rounded">Reset</button>
              <button onClick={handleZoomIn} className="px-2 py-1 hover:bg-white/20 rounded">＋</button>
              <span className="ml-2">{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={copyImageToClipboard}
                disabled={copyingImage}
                className={`ml-2 px-2 py-1 rounded ${copyingImage ? 'bg-white/20 text-white/70' : 'bg-white/20 hover:bg-white/30'}`}
                title="Copy image to clipboard"
              >
                {copyingImage ? 'Copying…' : 'Copy'}
              </button>
            </div>

            <div
              className={`w-full h-[80vh] flex items-center justify-center ${isZoomed ? 'cursor-move' : 'cursor-default'} overflow-hidden`}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseDown={handleMouseDown}
            >
              <img
                src={`data:image/jpeg;base64,${challenge.image_base64}`}
                alt="Challenge screenshot"
                className="max-w-full h-auto max-h-[80vh] object-contain select-none"
                style={{
                  transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                }}
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Activity Details Modal */}
      {showActivityDetails && selectedActivityDetails && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowActivityDetails(false);
              setSelectedActivityDetails(null);
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto relative">
            <button
              onClick={() => {
                setShowActivityDetails(false);
                setSelectedActivityDetails(null);
              }}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg transition-colors"
              title="Close"
            >
              ✕
            </button>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-12">Activity Details</h3>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {isJsonContent(selectedActivityDetails) ? (
                <pre className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg overflow-x-auto text-xs font-mono leading-relaxed">
                  <code className="text-gray-800 dark:text-gray-200">{selectedActivityDetails}</code>
                </pre>
              ) : (
                <div className="whitespace-pre-wrap bg-gray-50 dark:bg-gray-700 p-4 rounded-lg leading-relaxed font-sans">{selectedActivityDetails}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {showActivityLog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowActivityLog(false);
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden relative">
            <button
              onClick={() => setShowActivityLog(false)}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg transition-colors"
              title="Close"
            >
              ✕
            </button>
            <div className="mb-4 pr-12">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Activity Log</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Plain text format: &lt;Title&gt; then &lt;Details&gt;</p>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={() => {
                  if (!activityLogText) return;
                  copyTextToClipboard(activityLogText);
                }}
                className="px-3 py-1 text-xs rounded border text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                title="Copy to clipboard"
              >
                Copy
              </button>
              <button
                onClick={() => {
                  const text = activityLogText || '';
                  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const idFrag = challenge?.id ? challenge.id.substring(0, 8) : 'challenge';
                  a.href = url;
                  a.download = `${idFrag}-activity-log.txt`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
                className="px-3 py-1 text-xs rounded border text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                title="Download as .txt"
              >
                Download .txt
              </button>
            </div>
            <div className="overflow-auto max-h-[60vh] border rounded bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700">
              <pre className="p-4 text-xs whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">{activityLogText}</pre>
            </div>
          </div>
        </div>
      )}
      </div>
      <ChallengeAnalysisChat
        challenge={challenge}
        isOpen={isAnalysisOpen}
        onClose={() => setIsAnalysisOpen(false)}
        onAssistantMessage={handleAssistantMessage}
      />
    </>
  );
};

export default ChallengeDetailsPanel;
